/**
 * The write-back groups' contract — owner ruling DEC-105, bata-platform ADR 6054 (shipped in
 * #496, v0.58.0), as the pinned client generates it. `docs/adr/9042`.
 *
 * The execution pack and the backlog are read as GROUPS (by store, by kind, by run) and acted on as
 * a set: "exported", "executed as sent" or cancelled on a whole group or filter in ONE request —
 * never one POST per command.
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * `src/api/spec/openapi.json` is bata-platform v0.58.0, which publishes both routes (#496,
 * merged). So the types below are the generated ones, re-exported under the names the pages and tests already import (aliased where
 * the generated name differs), and `WRITE_BACK_GROUPS_READ` / `WRITE_BACK_GROUP_TRANSITION` are the
 * generated operations. Until this re-pin the file held the shapes typed by hand and the constants
 * were `null`; the generated shapes matched them field for field, except that the generator types
 * every query member optional and every optional member nullable — the page's query keeps
 * `group_by` and `country_key` required, and query and filter drop `null` (`WithoutNulls`) — and
 * types the act's `filter.fromState` / `filter.subjectKind` as the closed vocabularies rather than
 * `string`.
 *
 * The page's vocabularies stay here, because each word has words in the three catalogues; a
 * compile-time check holds each equal to the generated enum. The pack and backlog reads'
 * `store_key` / `subject_kind` filters are found on the pinned operations themselves
 * (`PACK_READS_TAKE_GROUP_FILTERS` in `src/features/inseason/write-back-groups.ts`) — this pin
 * carries them.
 *
 * `write-back-groups.test.tsx` keeps the tripwire: the pinned spec has each path exactly when its
 * constant is wired. `toState: 'acknowledged'` is "executed as sent" — the planner's confirmation
 * (ADR 6037 / R-16); `executed` is the target's own state and a planner never claims it.
 *
 * **A group's store is where the goods ARRIVE** — a proposal's door, a transfer's RECIPIENT, an exit
 * lane's destination; a price step has none and sits in the no-value bucket, selected by `-`
 * (`store_key=-` on the reads, `storeKey: '-'` on the act).
 */

import {
  listWriteBackGroups, recordWriteBackGroupTransition,
  type ListWriteBackGroupsParams, type PackStateFilter, type RecordWriteBackGroupTransitionParams,
  type WriteBackGroup, type WriteBackGroupDimension, type WriteBackGroupFilterBody, type WriteBackGroupPage,
  type WriteBackGroupSkip, type WriteBackGroupSkipReason, type WriteBackGroupTargetState, type WriteBackGroupTotals,
  type WriteBackGroupTransitionRequest, type WriteBackGroupTransitionResult, type WriteBackStateCode,
  type WriteBackSubjectKind,
} from '../generated';
import { NO_VALUE, type WithoutNulls } from './alert-groups';

export type {
  ListWriteBackGroupsParams, WriteBackGroup, WriteBackGroupDimension, WriteBackGroupPage, WriteBackGroupSkip,
  WriteBackGroupSkipReason, WriteBackGroupTotals, WriteBackGroupTransitionRequest, WriteBackGroupTransitionResult,
  WriteBackStateCode, WriteBackSubjectKind,
};

/** True exactly when `A` and `B` are the same type — the compile-time half of the vocabulary checks. */
type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;

/** The filter value that selects the commands with no store (price steps): `store_key=-`. */
export const WRITE_BACK_NO_STORE = NO_VALUE;

/** The kinds of write-back command — the generated `WriteBackSubjectKind`. */
export const WRITE_BACK_SUBJECT_KINDS = [
  'price_action', 'proposal', 'transfer', 'exit_consolidation_line',
  // bata-reqs ADR 9002 stage 3 (platform ADR 9049): a line of the one run's merged list.
  'move_line',
] as const satisfies readonly WriteBackSubjectKind[];
const SUBJECT_KINDS_MATCH: Equals<(typeof WRITE_BACK_SUBJECT_KINDS)[number], WriteBackSubjectKind> = true;

/**
 * The `state` filter of the groups read — one lifecycle state, or `open` for the backlog
 * (approved, dispatched or partially executed, from whatever run; bata-platform ADR 6105).
 * The generated `PackStateFilter`.
 */
export const WRITE_BACK_PACK_STATE_FILTERS = [
  'open', 'approved', 'dispatched', 'delivered', 'acknowledged', 'executed', 'rejected', 'unknown', 'partially_executed', 'cancelled',
] as const satisfies readonly PackStateFilter[];
const STATE_FILTERS_MATCH: Equals<(typeof WRITE_BACK_PACK_STATE_FILTERS)[number], PackStateFilter> = true;
export type WriteBackPackStateFilter = PackStateFilter;
const STATE_CODES_MATCH: Equals<Exclude<PackStateFilter, 'open'>, WriteBackStateCode> = true;

// ── the groups read ──────────────────────────────────────────────────────────

/** The dimensions a write-back group is cut by — DEC-105: store, kind, run. */
export const WRITE_BACK_GROUP_DIMENSIONS = ['store', 'subject_kind', 'run'] as const satisfies readonly WriteBackGroupDimension[];
const DIMENSIONS_MATCH: Equals<(typeof WRITE_BACK_GROUP_DIMENSIONS)[number], WriteBackGroupDimension> = true;

/** The read's query as the pages send it: the generated query, with `group_by` and `country_key` required. */
export type WriteBackGroupQuery = WithoutNulls<NonNullable<ListWriteBackGroupsParams['query']>> & {
  readonly group_by: WriteBackGroupDimension;
  readonly country_key: string;
};

export type WriteBackGroupsRead = typeof listWriteBackGroups;

// ── the group transition ─────────────────────────────────────────────────────

/** The set a transition takes — reconstructible from these fields alone (DEC-90). */
export type WriteBackGroupFilter = WithoutNulls<WriteBackGroupFilterBody>;

/** What a planner may move a set to: exported, confirmed executed as sent, cancelled. */
export const WRITE_BACK_GROUP_TO_STATES = ['dispatched', 'acknowledged', 'cancelled'] as const satisfies readonly WriteBackGroupTargetState[];
const TO_STATES_MATCH: Equals<(typeof WRITE_BACK_GROUP_TO_STATES)[number], WriteBackGroupTargetState> = true;
export type WriteBackGroupToState = WriteBackGroupTargetState;

/** Why a matched command was not moved — the platform's closed vocabulary (ADR 6054). */
export const WRITE_BACK_GROUP_SKIP_REASONS = ['out_of_order', 'already_in_state'] as const satisfies readonly WriteBackGroupSkipReason[];
const SKIP_REASONS_MATCH: Equals<(typeof WRITE_BACK_GROUP_SKIP_REASONS)[number], WriteBackGroupSkipReason> = true;

export type WriteBackGroupTransitionParams = RecordWriteBackGroupTransitionParams;

export type WriteBackGroupTransitionOp = typeof recordWriteBackGroupTransition;

/** 409: the filter matches other than `expectedCount` now; `currentCount` on the document. */
export const WRITE_BACK_GROUP_CHANGED = 'planning.inseason.write_back_group_changed';
/** 422: the set is above the bound; `currentCount` and `maximumCount` on the document. */
export const WRITE_BACK_GROUP_TOO_LARGE = 'planning.inseason.write_back_group_too_large';
/** 422: `toState: 'acknowledged'` without `executedAsSent: true` — never sent by this client. */
export const WRITE_BACK_GROUP_EXECUTED_AS_SENT_REQUIRED = 'planning.inseason.write_back_group_executed_as_sent_required';
/** 422: a cancellation reason outside the governed list (a known problem, `src/api/problem.ts`). */
export const WRITE_BACK_CANCELLATION_REASON_NOT_GOVERNED = 'planning.inseason.cancellation_reason_not_governed';

// ── the operations ───────────────────────────────────────────────────────────

/**
 * The generated operations. Typed `| null` still, so a test (and a component's `read`/`act` prop)
 * can mount the "not pinned" state by passing `null` explicitly.
 */
export const WRITE_BACK_GROUPS_READ: WriteBackGroupsRead | null = listWriteBackGroups;
export const WRITE_BACK_GROUP_TRANSITION: WriteBackGroupTransitionOp | null = recordWriteBackGroupTransition;

/** The vocabulary checks, held so the compiler reports a mismatch where it is declared. */
export const WRITE_BACK_GROUP_VOCABULARIES_MATCH = SUBJECT_KINDS_MATCH && STATE_FILTERS_MATCH && STATE_CODES_MATCH
  && DIMENSIONS_MATCH && TO_STATES_MATCH && SKIP_REASONS_MATCH;

/** The paths the re-pin tripwire looks for in the spec. */
export const WRITE_BACK_GROUPS_PATH_IN_SPEC = '/api/v1/inseason/write-back/groups';
export const WRITE_BACK_GROUP_TRANSITION_PATH_IN_SPEC = '/api/v1/inseason/write-back/group-transitions';
